import type { ProcessUploadResponse } from 'api';
import { addError, logger } from 'monitor';
import { useCallback, useMemo } from 'react';

import { useBulkDeleteEvidence, useOfflineEvidenceStore } from '../../../evidence';
import { type Bale } from '../../types';
import { compressImage } from '../../utils/compressImage';
import { createBaleFromFile } from '../../utils/createBaleFromFile';
import { validateFile } from '../../utils/files/validateFile/validateFile';
import { trackBaleSopOutcome } from '../../utils/trackBaleSopOutcome';
import { useUploadBale } from '../useUploadBale/useUploadBale';
import { useBaleAnalysisStore } from './useBaleAnalysisStore';

/**
 * @deprecated
 */
export const useBaleAnalysis = () => {
  const { mutateAsync: uploadBale } = useUploadBale();
  const {
    bales,
    mode,
    parentId,
    actions: { addBale, removeBale, getBale },
  } = useBaleAnalysisStore();
  const { removeEvidenceFile: removeOfflineEvidence } = useOfflineEvidenceStore();

  const isBaleLoading = useMemo(() => bales.some((bale) => bale.isLoading), [bales]);
  const isPhotoRejected = useMemo(() => bales.some((bale) => bale.analysisStatus === 'rejected-photo'), [bales]);
  const isMaterialRejected = useMemo(
    () => bales.some((bale) => bale.analysisStatus === 'rejected-material' && bale.loadingStatus === undefined),
    [bales]
  );

  const validateBale = useCallback(
    async (bale: Bale): Promise<Bale> => {
      const existingFiles = bales.map((b) => b.photo);
      const validation = await validateFile(bale.photo, {
        existingFiles,
        logContext: { baleId: bale.id },
      });

      if (!validation.isValid) {
        return {
          ...bale,
          analysisStatus: 'rejected-photo',
          photoRejectedReason: validation.rejectionReason,
        };
      }

      return bale;
    },
    [bales]
  );

  const addBalePhoto = useCallback(
    async ({ photo, model }: { photo: File; model?: string }): Promise<ProcessUploadResponse | null> => {
      logger.info('Adding bale photo', { photo });
      const processStartTime = Date.now();
      const bale = await validateBale(createBaleFromFile(photo));
      if (bale.analysisStatus === 'rejected-photo') {
        logger.info('Bale photo rejected', { bale, photoRejectedReason: bale.photoRejectedReason });
        trackBaleSopOutcome(bale, 'frontend');
        addBale(bale);
        return {
          ...bale,
          aluminium_composition: null,
          artifacts: [],
          created: false,
          hdpe_composition: null,
          image: {
            id: 0,
            url: '',
          },
          message: 'Rejected photo',
          model_results: {
            coco_prediction: [],
            model_name: String(model),
            model_version: '1.0',
            outputs: [
              {
                acceptance: false,
                features: {},
                highest_grade_name: 'Reject',
                image_rejected: true,
                image_rejection_reasons: [String(bale.photoRejectedReason)],
                reasons: [],
              },
            ],
          },
          pet_composition: null,
          pet_loose_composition: null,
          target_weight_percentage: null,
        };
      }

      try {
        logger.info('Compressing bale photo', { photo });
        const compressedPhoto = await compressImage(photo);
        logger.info('Bale photo compressed', {
          compressedSize: compressedPhoto.size,
          originalSize: photo.size,
          percentage: Math.round((compressedPhoto.size / photo.size) * 100),
        });
        bale.photo = compressedPhoto;

        logger.info('Uploading bale photo', { photo });
        const result = await uploadBale({ bale, model, startTime: processStartTime }); // TODO: upload only if user is online https://linear.app/safi/issue/0ba00154-2323-4096-9f0f-b92387f3c6c2

        return result;
      } catch (error) {
        addError(error);
        return null;
      }
    },
    [validateBale, addBale, uploadBale]
  );

  const retryAnalysis = useCallback(
    ({ baleId, model }: { baleId: string; model?: string }) => {
      const bale = getBale(baleId);
      if (!bale) {
        addError(new Error('Bale not found'));
        return;
      }

      uploadBale({ bale, model, startTime: Date.now() });
    },
    [uploadBale, getBale]
  );

  const { deleteEvidenceAsync: removeOnlineEvidence, isPending: isRemovePending } = useBulkDeleteEvidence({
    mode,
    onError: addError,
    parentId,
  });

  const removeBalePhoto = useCallback(
    (baleId: string, onSuccess?: () => void) => {
      const bale = getBale(baleId);
      if (!bale) {
        logger.error('Bale not found', { baleId }, Error('Bale not found'));
        return;
      }
      logger.info(`Removing local evidence ${baleId}`);
      removeBale(baleId);

      logger.info(`Removing offline evidence ${baleId}`);
      removeOfflineEvidence(bale.id);

      if (!bale?.evidenceId) {
        logger.info('Bale not analyzed, skipping online delete', { bale });
        return;
      }
      logger.info(`Deleting online evidence ${bale.evidenceId}`, {
        bale,
      });
      void removeOnlineEvidence([bale.evidenceId]).then((deleted) => {
        if (deleted) {
          onSuccess?.();
        }
      });
    },
    [removeBale, getBale, removeOnlineEvidence, removeOfflineEvidence]
  );

  return {
    addBalePhoto,
    bales,
    isBaleLoading,
    isMaterialRejected,
    isPhotoRejected,
    isRemovePending: isRemovePending,
    removeBalePhoto: removeBalePhoto,
    retryAnalysis,
  };
};
